import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { useAccessibility } from "../../accessibility";
import erezImg from "../../assets/erez.webp";

export function About() {
  const { state } = useAccessibility();
  const reduceMotion = state.reduceMotion;

  // Motion variants
  const fadeRight = reduceMotion
    ? {
        initial: { opacity: 1, x: 0 },
        whileInView: { opacity: 1, x: 0 },
        viewport: { once: true },
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0, x: 50 },
        whileInView: { opacity: 1, x: 0 },
        viewport: { once: true },
        transition: { duration: 0.8 },
      };

  const fadeUp = reduceMotion
    ? {
        initial: { opacity: 1, y: 0 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0 },
      }
    : {
        initial: { opacity: 0, y: 30 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true },
        transition: { duration: 0.8, delay: 0.2 },
      };

  return (
    <section
      id="about"
      aria-labelledby="about-title"
      className="py-24 relative overflow-hidden scroll-mt-16 md:scroll-mt-18
      bg-gradient-to-b from-slate-900/95 via-slate-950/90 to-slate-950/95 text-slate-200"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(59,130,246,0.05),transparent_60%)] pointer-events-none"
      />
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center md:items-center justify-center gap-16">
          {/* Portrait */}
          <motion.figure
            {...fadeRight}
            className="w-full md:w-[42%] lg:w-[38%] relative"
          >
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900 shadow-[0_25px_80px_rgba(0,0,0,0.6)] border border-slate-700">
              <img
                src={erezImg}
                alt="עו״ד ארז עזרא"
                width="600"
                height="900"
                className="w-full h-full object-cover object-center"
              />

              {/* Soft vignette */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
            </div>

            {/* Decorative element */}
            <div
              aria-hidden="true"
              className="absolute -bottom-10 -left-10 w-40 h-40 bg-blue-600/20 rounded-full opacity-40 blur-3xl"
            />
          </motion.figure>

          {/* Content */}
          <div className="w-full md:w-1/2 md:pr-12">
            <motion.div {...fadeUp}>
              <header>
                <h2
                  id="about-title"
                  className="text-4xl font-bold text-slate-100 mb-6"
                >
                  נעים להכיר, עו״ד ארז עזרא.
                </h2>
                <p
                  id="about-subtitle"
                  className="text-xl text-slate-300 font-medium mb-8"
                >
                  עורך דין שרואה את האדם מאחורי התיק.
                </p>
              </header>

              <article
                aria-labelledby="about-title"
                className="space-y-6 text-lg text-slate-300 font-light leading-relaxed"
              >
                <p>
                  אני מאמין שמאחורי כל תביעה משפטית עומד קודם כל אדם. כשאדם נפגע
                  בתאונה, חווה רשלנות או עומד מול המוסד לביטוח לאומי - הוא זקוק
                  ליותר מייצוג משפטי קר. הוא זקוק למישהו שישמע את הסיפור שלו
                  ויילחם עליו כאילו היה שלו.
                </p>

                <p className="text-white font-medium">
                  הסיפור שלך הוא המצפן שלנו.
                </p>

                <p>
                  מאז שהוסמכתי כעורך דין בשנת{" "}
                  <span className="text-white font-semibold">2007</span>, הצבתי
                  לעצמי מטרה ברורה: להעניק ללקוחותיי מעטפת מקצועית מהשורה
                  הראשונה, לצד ביטחון ושקט נפשי ברגעים המורכבים בחייהם.
                </p>

                <p>
                  המשרד, הממוקם בפתח תקווה, חרט על דגלו ערכים של שקיפות מלאה,
                  זמינות גבוהה ומאבק בלתי מתפשר למיצוי זכויותיכם.
                </p>

                <p className="text-white font-medium">
                  אנחנו כאן כדי לוודא שהסיפור שלך יסתיים בניצחון.
                </p>
              </article>

              <section aria-labelledby="values-title" className="relative">
                <h3 id="values-title" className="sr-only">
                  ערכי המשרד
                </h3>
                <ul
                  role="list"
                  aria-label="ערכי המשרד"
                  className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4"
                >
                  {[
                    "זמינות אישית גבוהה",
                    "שקיפות מלאה לאורך כל הדרך",
                    "נסיון מוכח בתיקים מורכבים",
                    "ללא פשרות על התוצאה הסופית",
                  ].map((item, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-slate-200 font-medium transition-all duration-300 hover:text-white"
                    >
                      <CheckCircle2
                        aria-hidden="true"
                        className="text-blue-500 drop-shadow-[0_0_10px_rgba(59,130,246,0.7)]"
                        size={20}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
